import { AxiosResponse } from 'axios';
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';

import { DataKiSyokenResponse } from '../domain/kiSyoken/types';

import {
  DataKiSyokenActionCreators,
  DataKiSyokenActionTypes,
  FetchDataKiSyokenAction,
  FetchDataKiSyokenFilteredKiTagAverageAction,
  FetchDataKiSyokenKiListAverageAction,
  FetchDataKiSyokenKiTagAverageAction,
} from '../redux/server/dataKiSyoken';
import { settingsOptionsKiSyokenSearchConditionSelector } from '../redux/server/settingsOptionsKiSyoken';
import {
  kiSyokenTableHeaderSubMenuMultiFilterSelector,
  selectKiSyokenTableHeaderSubMenuMultiFilterAction,
  selectKiSyokenTableHeaderSubMenuMultiFilterSelectOptionsAction,
} from '../redux/ui/kiSyokenSettings';
import { Api, buildConfig } from '../utils/api';
import {
  KISYOKEN_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER,
  buildPpmKbnMultiFilterSelectOptions,
  buildTypeMultiFilterSelectOptions,
  collectUniqueColumnValues,
  selectedValuesWithinOptions,
} from '../utils/tableHeaderSubMenuMultiFilter';
import { handleErrorSaga } from './errorSaga';

/**
 * dataKiSyokenのデータを取得する
 */
export function* fetchDataKiSyokenSaga(
  api: Api,
  action: FetchDataKiSyokenAction
) {
  try {
    yield put(DataKiSyokenActionCreators.fetchDataKiSyokenRequestAction());
    const response: AxiosResponse<DataKiSyokenResponse> = yield call(
      api.get,
      '/data/kiSyoken',
      buildConfig(action.payload.params)
    );

    const dataKiSyoken = response.data;

    yield put(
      DataKiSyokenActionCreators.fetchDataKiSyokenSuccessAction(dataKiSyoken)
    );

    const searchCondition: ReturnType<
      typeof settingsOptionsKiSyokenSearchConditionSelector
    > = yield select(settingsOptionsKiSyokenSearchConditionSelector);
    const sisTypeNames =
      searchCondition?.sisTypes.map((item) => item.name) ?? [];

    const newOptions =
      KISYOKEN_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER.reduce<
        Record<string, string[]>
      >((acc, code) => {
        const uniqueValues = collectUniqueColumnValues(
          [
            {
              columns: dataKiSyoken.data.columns,
              rows: dataKiSyoken.data.rows,
            },
          ],
          code
        );
        acc[code] =
          code === 'type'
            ? buildTypeMultiFilterSelectOptions(sisTypeNames, uniqueValues)
            : buildPpmKbnMultiFilterSelectOptions(uniqueValues);

        return acc;
      }, {});

    yield put(
      selectKiSyokenTableHeaderSubMenuMultiFilterSelectOptionsAction(newOptions)
    );

    const currentFilter: ReturnType<
      typeof kiSyokenTableHeaderSubMenuMultiFilterSelector
    > = yield select(kiSyokenTableHeaderSubMenuMultiFilterSelector);
    const selectedType = currentFilter?.type ?? [];
    const nextType = selectedValuesWithinOptions(
      selectedType,
      newOptions.type ?? []
    );
    if (currentFilter != null && selectedType.length !== nextType.length) {
      yield put(
        selectKiSyokenTableHeaderSubMenuMultiFilterAction({
          ...currentFilter,
          type: nextType,
        })
      );
    }
  } catch (error: unknown) {
    yield put(DataKiSyokenActionCreators.renewDataKiSyokenAction());
    yield fork(handleErrorSaga, error);
  }
}

/**
 * チェックされた機種のみの平均合計行データを取得する
 */
function* fetchDataKiSyokenKiListAverageSaga(
  api: Api,
  action: FetchDataKiSyokenKiListAverageAction
) {
  try {
    yield put(
      DataKiSyokenActionCreators.fetchDataKiSyokenKiListAverageRequestAction()
    );

    const params = {
      ...action.payload.params,
      containsAverage: true,
      containsData: false,
    };

    const response: AxiosResponse<DataKiSyokenResponse> = yield call(
      api.get,
      '/data/kiSyoken',
      buildConfig(params)
    );

    const dataKi = response.data;

    yield put(
      DataKiSyokenActionCreators.fetchDataKiSyokenKiListAverageSuccessAction(
        dataKi
      )
    );
  } catch (error: unknown) {
    yield put(DataKiSyokenActionCreators.renewDataKiSyokenAction());
    yield fork(handleErrorSaga, error);
  }
}

function* fetchDataKiSyokenKiFilteredTagAverageSaga(
  api: Api,
  action: FetchDataKiSyokenFilteredKiTagAverageAction
) {
  try {
    yield put(
      DataKiSyokenActionCreators.fetchDataKiSyokenFilteredKiTagAverageRequestAction()
    );

    const params = {
      ...action.payload.params,
      containsAverage: true,
      containsData: false,
    };

    const response: AxiosResponse<DataKiSyokenResponse> = yield call(
      api.get,
      '/data/kiSyoken',
      buildConfig(params)
    );
    const dataKi = response.data;

    yield put(
      DataKiSyokenActionCreators.fetchDataKiSyokenFilteredKiTagAverageSuccessAction(
        dataKi
      )
    );
  } catch (error: unknown) {
    yield put(DataKiSyokenActionCreators.renewDataKiSyokenAction());
    yield fork(handleErrorSaga, error);
  }
}

// 機種タグを選択した機種のみの平均合計行データを取得する（タグ別平均行用）
function* fetchDataKiSyokenKiTagsAverageSaga(
  api: Api,
  action: FetchDataKiSyokenKiTagAverageAction
) {
  try {
    yield put(
      DataKiSyokenActionCreators.fetchDataKiSyokenKiTagAverageRequestAction()
    );

    const params = {
      ...action.payload.params,
      containsAverage: true,
      containsData: false,
    };

    const response: AxiosResponse<DataKiSyokenResponse> = yield call(
      api.get,
      '/data/kiSyoken',
      buildConfig(params)
    );
    const dataKi = response.data;

    yield put(
      DataKiSyokenActionCreators.fetchDataKiSyokenKiTagAverageSuccessAction(
        dataKi
      )
    );
  } catch (error: unknown) {
    yield put(DataKiSyokenActionCreators.renewDataKiSyokenAction());
    yield fork(handleErrorSaga, error);
  }
}

function* handleFetchDataKiSyokenSaga(api: Api) {
  yield takeEvery(
    DataKiSyokenActionTypes.FETCH_DATA_KISYOKEN,
    fetchDataKiSyokenSaga,
    api
  );
}

function* handleFetchDataKiSyokenKiListAverageSaga(api: Api) {
  yield takeEvery(
    DataKiSyokenActionTypes.FETCH_DATA_KISYOKEN_KI_LIST_AVERAGE,
    fetchDataKiSyokenKiListAverageSaga,
    api
  );
}

function* handleFetchDataKiSyokenTagAverageSaga(api: Api) {
  yield takeEvery(
    DataKiSyokenActionTypes.FETCH_DATA_KISYOKEN_KI_TAG_AVERAGE,
    fetchDataKiSyokenKiTagsAverageSaga,
    api
  );
}

function* handleFetchDataKiSyokenFilteredTagAverageSaga(api: Api) {
  yield takeEvery(
    DataKiSyokenActionTypes.FETCH_DATA_KISYOKEN_KI_FILTERED_TAG_AVERAGE,
    fetchDataKiSyokenKiFilteredTagAverageSaga,
    api
  );
}

export function* dataKiSyokenSaga(context: { api: Api }) {
  yield fork(handleFetchDataKiSyokenSaga, context.api);
  yield fork(handleFetchDataKiSyokenKiListAverageSaga, context.api);
  yield fork(handleFetchDataKiSyokenTagAverageSaga, context.api);
  yield fork(handleFetchDataKiSyokenFilteredTagAverageSaga, context.api);
}
